import React, { useEffect, useState } from 'react'
import Modal from 'components/modal'
import Button from 'components/button'
import UserSelect from 'components/userSelect'
import api from 'helpers/api'
import { IOrganisation, IOrganisationUser } from 'types'
import { OrganisationRoles } from 'app-constants'
import { useApplicationStore } from 'hooks/application'

interface OrganisationAdminsModalProps {
  open: boolean
  onClose: () => void
  organisation: IOrganisation
  onChanged?: (organisation: IOrganisation) => void
}

const OrganisationAdminsModal: React.FC<OrganisationAdminsModalProps> = ({
  open,
  onClose,
  organisation,
  onChanged
}) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [saving, setSaving] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])

  const orgUsers = (organisation.users || []).map((u) => ({
    publicId: u.user.email,
    name: u.user.name || u.user.email,
    email: u.user.email,
    createdAt: u.user.createdAt
  }))

  // Seed with current admins when opened
  useEffect(() => {
    if (!open) return
    const currentAdminEmails = (organisation.users || [])
      .filter((u) => u.role === OrganisationRoles.ADMIN)
      .map((u) => u.user.email)
    setSelectedEmails(currentAdminEmails)
  }, [open, organisation.users])

  const toggleUser = (email: string) => {
    setSelectedEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  const canSave = selectedEmails.length > 0

  const handleSave = async () => {
    if (!canSave) {
      displayErrorMessage('Select at least one person to be an organisation admin.')
      return
    }

    const usersPayload: IOrganisationUser[] = selectedEmails
      .map((email) => (organisation.users || []).find((u) => u.user.email === email))
      .filter((ou): ou is NonNullable<typeof ou> => !!ou?.user?.email?.trim())
      .map((ou) => ({
        user: { email: ou.user.email },
        role: OrganisationRoles.ADMIN
      })) as unknown as IOrganisationUser[]

    setSaving(true)

    const payload: IOrganisation = {
      ...organisation,
      users: usersPayload.filter((u) => u.role !== OrganisationRoles.MEMBER)
    }

    api
      .updateOrganisation(organisation.publicId, payload)
      .then((resp) => {
        setSnackbarMessage?.({ status: 'success', message: 'Organisation users/roles updated' })
        setSaving(false)
        onChanged?.(resp.data as IOrganisation)
        onClose()
      })
      .catch((e) => {
        displayErrorMessage(e)
        setSaving(false)
      })
  }

  return (
    <Modal id="organisation-admins-modal" open={open} setOpen={onClose} title="Manage Admins">
      <div className="flex flex-col gap-4">
        <div className="text-sm text-muted">
          Select which organisation users should be admins. At least one admin is required.
        </div>

        <UserSelect
          users={orgUsers}
          selectedEmails={selectedEmails}
          onToggle={toggleUser}
          placeholder="Search by name or email..."
          emptyMessage="No organisation users found."
          showToolbar
          classNames={{ list: 'max-h-[300px]' }}
        />

        {selectedEmails.length === 0 && <span className="text-xs text-red">At least one admin is required.</span>}

        <div className="flex items-center justify-end gap-2">
          <Button internalType="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button internalType="accept" onClick={handleSave} isLoading={saving} disabled={!canSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default OrganisationAdminsModal
